<script>
import { mapGetters } from 'vuex';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { getInboxClassByType, inboxDisplayName } from 'dashboard/helper/inbox';
import Thumbnail from '../Thumbnail.vue';
import MessagePreview from './MessagePreview.vue';
import router from '../../../routes';
import { frontendURL, conversationUrl } from '../../../helper/URLHelper';
import inboxMixin from 'shared/mixins/inboxMixin';
import ConversationContextMenu from './contextMenu/Index.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import PriorityMark from './PriorityMark.vue';
import { CONVERSATION_PRIORITY } from 'shared/constants/messages';
import SLACardLabel from './components/SLACardLabel.vue';
import StatusBadge from '../../ui/StatusBadge.vue';

export default {
  components: {
    CardLabels,
    Thumbnail,
    ConversationContextMenu,
    TimeAgo,
    MessagePreview,
    PriorityMark,
    SLACardLabel,
    StatusBadge,
  },
  mixins: [inboxMixin],
  props: {
    activeLabel: {
      type: String,
      default: '',
    },
    chat: {
      type: Object,
      default: () => {},
    },
    hideInboxName: {
      type: Boolean,
      default: false,
    },
    hideThumbnail: {
      type: Boolean,
      default: false,
    },
    teamId: {
      type: [String, Number],
      default: 0,
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    showAssignee: {
      type: Boolean,
      default: false,
    },
    conversationType: {
      type: String,
      default: '',
    },
    selected: {
      type: Boolean,
      default: false,
    },
    enableContextMenu: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hovered: false,
      showContextMenu: false,
      contextMenu: {
        x: null,
        y: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
      callInfo: 'webphone/getCallInfo',
    }),
    bulkActionCheck() {
      return !this.hideThumbnail && !this.hovered && !this.selected;
    },

    // Urgente e o unico nivel que ganha tratamento no card inteiro. Os outros
    // quatro ficam no chip: tingir a linha em quatro cores transformaria a
    // lista num semaforo e nenhum nivel saltaria.
    isUrgentPriority() {
      return this.chat.priority === CONVERSATION_PRIORITY.URGENT;
    },
    chatMetadata() {
      return this.chat.meta || {};
    },

    assignee() {
      return this.chatMetadata.assignee || {};
    },

    // O checkbox nao tem rotulo visivel -- so aparece no hover, no lugar do
    // avatar. Serve de tooltip e de aria-label, e acompanha o estado: parado
    // em "Selecionar" ele mentiria sobre o que o clique faz numa linha ja
    // marcada.
    selectionTooltip() {
      return this.selected
        ? this.$t('BULK_ACTION.DESELECT_CONVERSATION_TOOLTIP')
        : this.$t('BULK_ACTION.SELECT_CONVERSATION_TOOLTIP');
    },

    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.chatMetadata.sender.id
      );
    },

    isActiveChat() {
      return this.currentChat.id === this.chat.id;
    },

    unreadCount() {
      return this.chat.unread_count;
    },

    hasUnread() {
      return this.unreadCount > 0;
    },

    lastMessageInChat() {
      return getLastMessage(this.chat);
    },

    inbox() {
      const { inbox_id: inboxId } = this.chat;
      const stateInbox = this.$store.getters['inboxes/getInbox'](inboxId);
      return stateInbox;
    },

    showInboxName() {
      return !this.hideInboxName && this.inbox && this.inbox.name;
    },
    // O marcador da caixa de teste e recomposto aqui, e nao lido do nome
    // gravado: assim ele acompanha o idioma escolhido.
    inboxName() {
      return inboxDisplayName(
        this.inbox,
        this.$t('CONVERSATION.SANDBOX_INBOX_LABEL')
      );
    },
    inboxIcon() {
      const inbox = this.inbox || {};
      return getInboxClassByType(inbox.channel_type, inbox.phone_number);
    },
    hasSlaPolicyId() {
      return this.chat?.sla_policy_id;
    },
    hasActiveCall() {
      const { id, chat_id: chatId } = this.callInfo;
      return Boolean(id) && chatId === this.chat.id;
    },
    callStatusMessage() {
      const statusMessages = {
        accept: this.$t('WEBPHONE.ACTIVE'),
        terminate: this.$t('WEBPHONE.TERMINATE'),
        reject: this.$t('WEBPHONE.REJECTED'),
        outgoing_calling: this.$t('WEBPHONE.CONNECT_CALLING'),
        preaccept: this.$t('WEBPHONE.CALLING'),
      };
      return statusMessages[this.callInfo.status] || '';
    },
    currentContactDisplayName() {
      return this.currentContact.name || this.$t('EDIT_CONTACT.UNNAMED');
    },
  },
  methods: {
    onCardClick(e) {
      const { activeInbox, chat } = this;
      const path = frontendURL(
        conversationUrl({
          accountId: this.accountId,
          activeInbox,
          id: chat.id,
          label: this.activeLabel,
          teamId: this.teamId,
          foldersId: this.foldersId,
          conversationType: this.conversationType,
        })
      );

      if (e.metaKey || e.ctrlKey) {
        window.open(
          window.chatwootConfig.hostURL + path,
          '_blank',
          'noopener noreferrer nofollow'
        );
        return;
      }
      if (this.isActiveChat) {
        return;
      }

      router.push({ path });
    },
    onCardHover() {
      this.hovered = !this.hideThumbnail;
    },
    onCardLeave() {
      this.hovered = false;
    },
    onSelectConversation(checked) {
      const action = checked ? 'selectConversation' : 'deSelectConversation';
      this.$emit(action, this.chat.id, this.inbox.id);
    },
    openContextMenu(e) {
      if (!this.enableContextMenu) return;
      e.preventDefault();
      this.$emit('contextMenuToggle', true);
      this.contextMenu.x = e.clientX;
      this.contextMenu.y = e.clientY;
      this.showContextMenu = true;
    },
    closeContextMenu() {
      this.$emit('contextMenuToggle', false);
      this.showContextMenu = false;
      this.contextMenu.x = null;
      this.contextMenu.y = null;
    },
    onUpdateConversation(status, snoozedUntil) {
      this.closeContextMenu();
      this.$emit(
        'updateConversationStatus',
        this.chat.id,
        status,
        snoozedUntil
      );
    },
    async onAssignAgent(agent) {
      this.$emit('assignAgent', agent, [this.chat.id]);
      this.closeContextMenu();
    },
    // Clicar numa etiqueta ja aplicada tira ela.
    //
    // Quem decide e o card, nao o menu: aqui esta a conversa, entao a intencao
    // sai do estado real dela e nao de um espelho que poderia divergir. O menu
    // so precisa saber o que mostrar marcado.
    async onAssignLabel(label) {
      const aplicada = (this.chat.labels || []).includes(label.title);

      this.$emit(
        'assignLabel',
        {
          labels: [label.title],
          target: 'conversations',
          action: aplicada ? 'remove' : 'add',
        },
        [this.chat.id]
      );
      this.closeContextMenu();
    },
    async onAssignTeam(team) {
      this.$emit('assignTeam', team, this.chat.id);
      this.closeContextMenu();
    },
    async markAsUnread() {
      this.$emit('markAsUnread', this.chat.id);
      this.closeContextMenu();
    },
    async assignPriority(priority) {
      this.$emit('assignPriority', priority, this.chat.id);
      this.closeContextMenu();
    },
    togglePin(pinned) {
      this.$emit('togglePin', pinned, this.chat.id);
      this.closeContextMenu();
    },
  },
};
</script>

<template>
  <div
    class="relative flex flex-col w-full max-w-full px-5 py-4 cursor-pointer conversation bg-surface border-b border-line-faint hover:bg-surface-hover transition-colors duration-150"
    :class="{
      active: isActiveChat,
      'unread-chat': hasUnread,
      'conversation-selected': selected,
      'priority-urgent': isUrgentPriority,
    }"
    @mouseenter="onCardHover"
    @mouseleave="onCardLeave"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <!-- Avatar e texto so dividem as DUAS primeiras linhas. A linha de meta
         fica fora deste bloco, na largura inteira do card: o avatar tem 40px e
         o texto ao lado passa disso, entao prender a meta na coluna da direita
         deixava um vazio embaixo do avatar e ainda espremia os chips em 52px a
         menos de largura -- justo a linha que mais disputa espaco. -->
    <div class="flex items-start gap-3">
      <label
        v-if="hovered || selected"
        v-tooltip.right="selectionTooltip"
        class="checkbox-wrapper"
        @click.stop
      >
        <input
          :value="selected"
          :checked="selected"
          :aria-label="selectionTooltip"
          class="checkbox"
          type="checkbox"
          @change="onSelectConversation($event.target.checked)"
        />
      </label>
      <Thumbnail
        v-if="bulkActionCheck"
        :src="currentContact.thumbnail"
        :badge="inboxBadge"
        :username="currentContactDisplayName"
        :status="currentContact.availability_status"
        size="40px"
      />
      <div class="flex-1 min-w-0">
        <div class="flex items-center justify-between gap-2">
          <h4
            class="flex-1 min-w-0 m-0 text-sm font-semibold capitalize truncate conversation--user text-body"
          >
            {{ currentContactDisplayName }}
          </h4>
          <div class="flex items-center gap-1.5 shrink-0">
            <fluent-icon
              v-if="chat.pinned"
              v-tooltip="$t('CONVERSATION.CARD_CONTEXT_MENU.PIN.PINNED')"
              icon="pin"
              type="solid"
              size="14"
              class="shrink-0 text-muted"
              role="img"
              :aria-label="$t('CONVERSATION.CARD_CONTEXT_MENU.PIN.PINNED')"
            />
            <PriorityMark :priority="chat.priority" />
            <StatusBadge :status="chat.status" size="small" />
          </div>
        </div>

        <div
          v-if="hasActiveCall"
          class="flex items-center min-w-0 mt-0.5 gap-1 text-sm"
        >
          <fluent-icon
            size="16"
            class="shrink-0 text-green-700 dark:text-green-300"
            icon="call"
          />
          <span class="truncate text-green-700 dark:text-green-300">
            {{ $t('WEBPHONE.VOICE_CALL') }} - {{ $t('WEBPHONE.CALLING') }}
          </span>
          <span v-if="callStatusMessage" class="truncate text-body">
            {{ callStatusMessage }}
          </span>
        </div>
        <div v-else class="flex items-center gap-2 mt-0.5">
          <MessagePreview
            v-if="lastMessageInChat"
            :message="lastMessageInChat"
            class="flex-1 min-w-0 text-sm leading-5 conversation--message text-muted"
          />
          <p
            v-else
            class="flex-1 min-w-0 m-0 text-sm leading-5 truncate conversation--message text-muted"
          >
            <fluent-icon
              size="16"
              class="-mt-0.5 align-middle inline-block text-muted"
              icon="info"
            />
            <span>
              {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
            </span>
          </p>
          <span v-if="unreadCount > 0" class="unread">
            {{ unreadCount > 9 ? '9+' : unreadCount }}
          </span>
        </div>
      </div>
    </div>

    <div class="flex items-center min-w-0 gap-1.5 mt-1">
      <span v-if="showInboxName" class="meta-chip" :title="inboxName">
        <fluent-icon :icon="inboxIcon" size="12" class="shrink-0" />
        <span class="truncate">{{ inboxName }}</span>
      </span>
      <!-- Sem responsavel, sem chip. Um badge dizendo "nao atribuida" gasta a
           mesma largura de um nome e nao acrescenta nada que a ausencia dele ja
           nao diga -- e a linha de meta e justamente onde falta espaco. -->
      <span
        v-if="showAssignee && assignee.name"
        class="meta-chip"
        :title="assignee.name"
      >
        <!-- Foto so, nunca iniciais: o Avatar deriva a fonte de `size / 2.5`,
               entao a 16px as duas letras sairiam a 6px. Sem foto vale mais o
               ponto, ja que o nome do agente esta logo ao lado. -->
        <Thumbnail
          v-if="assignee.thumbnail"
          :src="assignee.thumbnail"
          :username="assignee.name"
          size="16px"
        />
        <span v-else class="w-1.5 h-1.5 rounded-full shrink-0 bg-subtle" />
        <span class="truncate">{{ assignee.name }}</span>
      </span>
      <!-- O SLA fica FORA do CardLabels. Dentro dele, no slot `before`, ele
             herdava o `overflow-hidden` de um container que nesta linha pode
             chegar a zero de largura -- e o aviso de SLA estourado, que e o
             sinal mais forte da lista, sumia junto com as etiquetas. -->
      <SLACardLabel v-if="hasSlaPolicyId" :chat="chat" class="shrink-0" />
      <CardLabels
        :conversation-labels="chat.labels"
        class="grow shrink-0 basis-32 min-w-0"
      />
      <TimeAgo
        compact
        :last-activity-timestamp="chat.timestamp"
        :created-at-timestamp="chat.created_at"
        class="shrink-0 ltr:ml-auto rtl:mr-auto conversation--time"
      />
    </div>
    <woot-context-menu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :pinned="!!chat.pinned"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        @updateConversation="onUpdateConversation"
        @assignAgent="onAssignAgent"
        @assignLabel="onAssignLabel"
        @assignTeam="onAssignTeam"
        @markAsUnread="markAsUnread"
        @assignPriority="assignPriority"
        @togglePin="togglePin"
      />
    </woot-context-menu>
  </div>
</template>

<style lang="scss" scoped>
// Os fundos de estado ficam aqui, e nao em utilities no `class` do template, de
// proposito: a linha traz `hover:bg-surface-hover`, que tem a mesma
// especificidade de qualquer `bg-*` vindo do `:class` -- qual das duas vence
// dependeria da ordem em que o Tailwind emite as cores no CSS. Com
// `.conversation.active` a especificidade decide, e a conversa aberta nao pisca
// para cinza quando o mouse passa por cima.
.conversation {
  &.active {
    @apply bg-brand-subtle;

    // Faixa da marca colada na borda de inicio, ocupando a altura toda. Como
    // pseudo-elemento ela nao entra no fluxo e nao desloca o conteudo.
    &::before {
      content: '';
      @apply absolute inset-y-0 ltr:left-0 rtl:right-0 w-[3px] bg-brand;
    }

    // O terciario nao aguenta o fundo tingido: `--fd-subtle` sobre
    // `--fd-brand-subtle` da 4.12:1 e reprova no AA. E a mesma conta que fez o
    // _design-tokens.scss recusar o flipdesk-600 para texto (4.13:1 sobre este
    // fundo). `muted` da 7.08:1. So no claro o problema existe, mas o token
    // resolve os dois temas.
    ::v-deep .conversation--time {
      @apply text-muted;
    }

    // `bg-brand-subtle` e o fundo desta linha E o fundo do badge de "Adiado"
    // (StatusBadge `--snoozed`). Sem isto o pill dissolve e sobra texto azul
    // solto onde os outros tres status mostram pastilha.
    ::v-deep .status-badge--snoozed {
      @apply bg-surface;
    }
  }

  // Prioridade urgente: o MESMO desenho do estado ativo -- faixa de 3px na
  // borda de inicio e fundo tingido --, na cor do chip de urgente (#dc2626).
  // Vale com a conversa aberta ou nao, porque o que ela marca nao e "onde
  // voce esta", e sim "esta aqui precisa de voce antes das outras".
  //
  // A conta do tingimento e a mesma que o _semantic-tokens.scss usa para o
  // `brand-subtle`: a cor a 10% sobre a superficie do tema. No claro
  // (255 255 255) da 252 233 233; no escuro (21 23 24) da 41 25 25. Valor
  // resolvido, e nao alfa: com alfa o vermelho se somaria ao azul do estado
  // ativo e a linha aberta e urgente sairia roxa.
  //
  // Vem DEPOIS do `.active` de proposito: mesma especificidade nos dois
  // `::before`, entao quem decide e a ordem, e a faixa vermelha tem de vencer
  // a azul. E vem ANTES da selecao em massa, que continua sendo o tingimento
  // mais forte da lista -- marcar linhas para uma acao em lote nao pode ficar
  // ambiguo.
  &.priority-urgent {
    background-color: rgb(252 233 233);

    &::before {
      content: '';
      @apply absolute inset-y-0 ltr:left-0 rtl:right-0 w-[3px];

      background-color: #dc2626;
    }

    // O `hover:bg-surface-hover` da linha do template perde para esta regra
    // (ela tem o atributo de escopo), entao sem isto a linha urgente ficaria
    // sem retorno nenhum ao passar o mouse. 14% sobre a superficie.
    &:hover {
      background-color: rgb(250 225 225);
    }

    // Aberta E urgente: o vermelho continua, um degrau mais forte (18%), para
    // a linha aberta nao sumir no meio das outras urgentes.
    &.active {
      background-color: rgb(250 216 216);
    }
  }

  .dark &.priority-urgent {
    background-color: rgb(41 25 25);

    &:hover {
      background-color: rgb(49 25 26);
    }

    &.active {
      background-color: rgb(57 26 27);
    }
  }

  // Selecao em massa tem tingimento proprio e vem depois do ativo: quando a
  // linha aberta tambem esta marcada, e a selecao que precisa saltar aos olhos
  // (a faixa da marca continua indicando qual esta aberta).
  //
  // 75 e nao 50: `primary-50` e `brand-subtle` sao os DOIS `--color-flipdesk-50`
  // (230 242 255), entao com o 50 a linha selecionada saia exatamente da cor da
  // linha aberta no tema claro -- e marcar a propria linha aberta nao mudava
  // pixel nenhum. O 75 (203 228 255) e um degrau abaixo e separa os dois.
  &.conversation-selected {
    @apply bg-primary-75 dark:bg-primary-950;
  }

  &.unread-chat {
    .conversation--user {
      @apply font-bold;
    }
    .conversation--message {
      @apply font-semibold text-body;
    }
  }

  // Painel de contato (ContactConversations): sem avatar, a linha aproveita a
  // largura inteira do acordeao -- sobra so a folga da faixa do estado ativo.
  // A ultima linha perde a divisoria para nao cortar o canto arredondado do
  // acordeao. O `:last-child` fica preso ao `.compact` de proposito: na lista
  // virtualizada cada linha e filha unica do wrapper da biblioteca, entao um
  // `:last-child` solto casaria com TODAS e apagaria todas as divisorias.
  &.compact {
    @apply pl-2;

    &:last-child {
      @apply border-b-0;
    }
  }

  // 40px para casar com o Thumbnail que ele substitui no hover: trocar um pelo
  // outro nao pode mexer no layout da linha.
  //
  // O `m-0` e o que garante isso, e nao e redundante: este wrapper e um
  // <label>, e assets/scss/widgets/_base.scss:39 da `mb-1` a TODO label do app
  // (regra pensada para rotulo de formulario). Como item de flex sob
  // `items-start`, quem manda na altura da linha e a margin box -- 40 + 4 =
  // 44px contra os 42px da coluna de texto. Resultado: a linha crescia 2px
  // exatamente ao passar o mouse, quando o avatar dava lugar ao checkbox.
  .checkbox-wrapper {
    @apply flex items-center justify-center w-10 h-10 m-0 rounded-avatar shrink-0
      cursor-pointer hover:bg-primary-100 dark:hover:bg-primary-900
      transition-colors duration-200;

    input[type='checkbox'] {
      @apply m-0 cursor-pointer;
    }
  }

  .unread {
    @apply h-5 leading-5 px-1.5 min-w-[1.25rem] rounded-full shrink-0
      text-center text-xxs font-bold text-white bg-primary-600
      dark:bg-primary-500;
  }

  // Mesma geometria do `woot-label small`, para os chips de metadado ficarem
  // alinhados com as etiquetas do CardLabels na mesma linha.
  //
  // A borda nao e enfeite: no tema claro `--fd-surface-sunken` e
  // `--fd-surface-hover` valem os DOIS `241 243 245`, entao o preenchimento do
  // chip some no hover da linha -- e hover e o estado dominante enquanto se
  // varre a lista. O contorno sobrevive aos dois fundos.
  //
  // O teto de largura existe para o CardLabels nao ser espremido a zero, mas
  // 7rem (112px) era um teto RIGIDO: cortava "Suporte WhatsApp Comercial" em
  // "Suporte What..." em toda linha, mesmo com a linha vazia a direita, e duas
  // inboxes de nome parecido viravam o mesmo texto truncado.
  //
  // Agora quem garante espaco as etiquetas e o proprio flex: o CardLabels tem
  // `basis-32 shrink-0`, ou seja, um piso de 8rem que ele nao cede. Sobrando
  // largura, o chip usa o que precisa; faltando, ele e quem encolhe -- e ja
  // trunca com tooltip. O teto proporcional so impede que um nome
  // desproporcional tome a linha toda.
  .meta-chip {
    @apply inline-flex items-center h-5 gap-1 px-2 min-w-0 max-w-[60%]
      rounded-full border border-line bg-surface-sunken text-muted text-xxs
      font-medium;
  }
}
</style>
